import React, { useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import { BLOCKS } from '@contentful/rich-text-types'

import type { FC, PropsWithChildren } from 'react'
import type { Options as RichTextRenderOptions } from '@contentful/rich-text-react-renderer'

import type * as LightboxTypes from '@segment/matcha/components/Lightbox/useLightbox'

import useLightbox from '@segment/matcha/components/Lightbox/useLightbox'
import Lightbox from '@segment/matcha/components/Lightbox'
import FigureBase, * as Figure from '@segment/matcha/components/Figure'

import CopyTextBase from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import RichText, { ColoredTypography } from '@segment/matcha/recipes/RichText'

import type { Integration } from '@segment/api/staticSiteBackend/integration'

import contentfulImageLoader from '@segment/utils/contentfulImageLoader'
import usePortal from '@segment/utils/usePortal'

interface HowItWorksProps {
  integration: Integration | undefined
}

const HowItWorks: FC<PropsWithChildren<HowItWorksProps>> = ({ integration }) => {
  const hasGallery = Boolean(integration?.integrationScreenshots?.length)
  const options: RichTextRenderOptions = {
    renderNode: {
      [BLOCKS.HEADING_2](_, children) {
        return <Heading variant="h2">{children}</Heading>
      },
    },
  }

  const withoutSegment = useMemo(() => {
    if (integration?.withoutSegment) {
      return <RichText data={integration?.withoutSegment} options={options} />
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integration])

  const items: GalleryItem[] =
    integration?.integrationScreenshots?.map<GalleryItem>((card) => ({
      src: card.fields?.file?.url,
      imgName: card.fields?.file?.fileName,
      caption: card.fields?.description,
    })) || []

  return (
    <HowItWorksStyled>
      <SectionBaseStyled variant="centered" copyVariant="wide" hasGallery={hasGallery}>
        <SectionCopyStyled>
          <CopyTextBase>{withoutSegment}</CopyTextBase>
        </SectionCopyStyled>
        <SectionGridContent gridSize={items.length}>
          <Gallery items={items} />
        </SectionGridContent>
      </SectionBaseStyled>
    </HowItWorksStyled>
  )
}

interface GalleryProps {
  items: GalleryItem[]
}
interface GalleryItem extends LightboxTypes.Image {
  imgName: string | undefined
}

const Gallery: FC<PropsWithChildren<GalleryProps>> = ({ items }) => {
  const lightBoxRef = useRef(null)
  const lightboxRoot = usePortal(`lightbox-root`)

  const { opened, active, images, previous, next, close, open } = useLightbox({
    initialOpen: false,
    images: items,
  })

  return (
    <>
      {(items &&
        items.map((content: GalleryItem, idx: number) => {
          return (
            <FigureBase
              overrides={{
                sm: `horizontal`,
                lg: `vertical`,
              }}
              key={content.imgName}
              gridMaxSize="100%"
            >
              <Figure.Image
                interactive={true}
                loader={contentfulImageLoader}
                onClick={() => open(idx)}
                alt={content.caption}
                height={257}
                width={450}
                src={content.src}
                loading="lazy"
              />
              <Figure.Caption>{content.caption}</Figure.Caption>
            </FigureBase>
          )
        })) ||
        null}
      {opened &&
        createPortal(
          <Lightbox ref={lightBoxRef} active={active} images={images} next={next} previous={previous} close={close} />,
          lightboxRoot
        )}
    </>
  )
}

const HowItWorksStyled = styled.div``

const Heading = styled(ColoredTypography)`
  text-align: center;

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    margin-bottom: 60px;
  }
`
interface WrapperProps {
  hasGallery: boolean
}
const SectionBaseStyled = styled(SectionBase)<WrapperProps>`
  ${({ hasGallery }) =>
    !hasGallery &&
    css`
      ${Section.Wrapper} {
        row-gap: 0;
      }
    `}
`

const SectionCopyStyled = styled(Section.Copy)`
  max-width: 880px;
`
interface SectionGridProps {
  gridSize: number
}
const SectionGridContent = styled(Section.Contents)<SectionGridProps>`
  margin: auto;
  display: flex;
  flex-direction: column;
  gap: 32px;
  align-items: center;
  max-width: 420px;

  ${({ theme }) => theme.breakpoints.up(`sm`)} {
    max-width: 100%;
  }

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    display: grid;
    gap: 48px;
    grid-auto-rows: minmax(100px, auto);
    max-width: 1420px;
    align-items: start;

    ${({ gridSize }) => css`
      grid-template-columns: repeat(${gridSize}, 1fr);
    `}

    ${({ gridSize }) =>
      gridSize === 1
        ? css`
            max-width: 450px;
          `
        : gridSize === 2 &&
          css`
            max-width: 950px;
          `}
  }
`

export default HowItWorks
